import { z } from 'zod';

import { Id, IsoDateTime, Slug } from '../common/primitives';

export const SponsoredPlacementSchema = z.enum([
  'HOMEPAGE_SHOWCASE',
  'HOMEPAGE_BANNER',
  'CATEGORY',
  'SEARCH',
  'PRODUCT',
  'VENDOR_PAID',
]);
export type SponsoredPlacement = z.infer<typeof SponsoredPlacementSchema>;

export const SponsoredThemeSchema = z.enum([
  'DARK',
  'LUXURY',
  'ORGANIC',
  'FESTIVAL',
  'PREMIUM_GOLD',
  'CUSTOM',
]);
export type SponsoredTheme = z.infer<typeof SponsoredThemeSchema>;

const Url = z.string().trim().min(1).max(2048);

export const SponsoredCollectionSchema = z.object({
  id: Id,
  campaignId: Id,
  collectionTitle: z.string(),
  thumbnailImage: z.string(),
  redirectUrl: z.string(),
  sortOrder: z.number().int(),
  isActive: z.boolean(),
  createdAt: IsoDateTime,
});
export type SponsoredCollection = z.infer<typeof SponsoredCollectionSchema>;

export const SponsoredCampaignSchema = z.object({
  id: Id,
  slug: z.string(),
  title: z.string(),
  subtitle: z.string().nullable(),
  sponsorName: z.string(),
  sponsorLogo: z.string().nullable(),
  heroImage: z.string(),
  productImage: z.string().nullable(),
  ctaText: z.string(),
  ctaUrl: z.string(),
  couponCode: z.string().nullable(),
  offerLabel: z.string().nullable(),
  backgroundTheme: z.string(),
  themeMeta: z.record(z.string(), z.any()).nullable(),
  placement: SponsoredPlacementSchema,
  vendorId: z.string().nullable(),
  isActive: z.boolean(),
  isFeatured: z.boolean(),
  startDate: IsoDateTime.nullable(),
  endDate: IsoDateTime.nullable(),
  priority: z.number().int(),
  totalViews: z.number().int(),
  totalClicks: z.number().int(),
  conversions: z.number().int(),
  revenuePaise: z.number().int(),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
  collections: z.array(SponsoredCollectionSchema).optional(),
});
export type SponsoredCampaign = z.infer<typeof SponsoredCampaignSchema>;

export const CollectionInput = z
  .object({
    id: Id.optional(),
    collectionTitle: z.string().trim().min(1).max(120),
    thumbnailImage: Url,
    redirectUrl: Url.default('/'),
    sortOrder: z.number().int().min(0).max(999).default(0),
    isActive: z.boolean().default(true),
  })
  .strict();
export type CollectionInput = z.infer<typeof CollectionInput>;

export const CreateSponsoredCampaignRequest = z
  .object({
    title: z.string().trim().min(2).max(140),
    subtitle: z.string().trim().max(280).optional(),
    slug: Slug.optional(),
    sponsorName: z.string().trim().min(2).max(120),
    sponsorLogo: Url.optional(),
    heroImage: Url,
    productImage: Url.optional(),
    ctaText: z.string().trim().min(1).max(40).default('Shop Now'),
    ctaUrl: Url.default('/'),
    couponCode: z.string().trim().max(40).optional(),
    offerLabel: z.string().trim().max(80).optional(),
    backgroundTheme: SponsoredThemeSchema.default('PREMIUM_GOLD'),
    themeMeta: z.record(z.string(), z.any()).optional(),
    placement: SponsoredPlacementSchema.default('HOMEPAGE_SHOWCASE'),
    vendorId: z.string().optional(),
    isActive: z.boolean().default(true),
    isFeatured: z.boolean().default(false),
    startDate: z.string().datetime().optional(),
    endDate: z.string().datetime().optional(),
    priority: z.number().int().min(0).max(1000).default(100),
    collections: z.array(CollectionInput).max(20).optional(),
  })
  .strict();
export type CreateSponsoredCampaignRequest = z.infer<typeof CreateSponsoredCampaignRequest>;

export const UpdateSponsoredCampaignRequest = CreateSponsoredCampaignRequest.partial().strict();
export type UpdateSponsoredCampaignRequest = z.infer<typeof UpdateSponsoredCampaignRequest>;

export const ListCampaignsQuery = z
  .object({
    status: z.enum(['all', 'active', 'draft', 'scheduled', 'expired']).default('all'),
    placement: SponsoredPlacementSchema.optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
  })
  .strict();
export type ListCampaignsQuery = z.infer<typeof ListCampaignsQuery>;
